import React, { useEffect, useState } from 'react';
import { api, fileToBase64 } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { FileEdit, Plus, Eye, Paperclip, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CR_STATUSES = ['Submitted', 'Under Review', 'Approved', 'Rejected', 'Implemented'];
const STATUS_COLOR = {
  Submitted: 'border-amber-500/40 text-amber-200',
  'Under Review': 'border-sky-500/40 text-sky-300',
  Approved: 'border-emerald-500/40 text-emerald-300',
  Rejected: 'border-red-500/40 text-red-300',
  Implemented: 'border-purple-500/40 text-purple-300',
};

export default function ChangeRequestsPage() {
  const { user, isManager, isClient, isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [decision, setDecision] = useState({ status: 'Under Review', decision_note: '' });
  const [form, setForm] = useState({ project_id: '', title: '', description: '', files: [] });

  const load = async () => {
    const res = await api.get('/change-requests');
    setItems(res.data);
  };
  useEffect(() => {
    load();
    api.get('/projects').then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    const attachments = [];
    for (const f of form.files) {
      const b64 = await fileToBase64(f);
      attachments.push({ filename: f.name, mime_type: f.type || 'application/octet-stream', data_base64: b64 });
    }
    await api.post('/change-requests', { project_id: form.project_id, title: form.title, description: form.description, attachments });
    setOpen(false);
    setForm({ project_id: '', title: '', description: '', files: [] });
    load();
  };

  const openDetail = async (cr) => {
    const res = await api.get(`/change-requests/${cr.id}`);
    setDetail(res.data);
    setDecision({ status: res.data.status === 'Submitted' ? 'Under Review' : res.data.status, decision_note: res.data.decision_note || '' });
  };

  const decide = async () => {
    await api.post(`/change-requests/${detail.id}/decision`, decision);
    setDetail(null);
    load();
  };

  const remove = async (cr, closeDetail = false) => {
    if (!window.confirm(`Delete change request "${cr.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/change-requests/${cr.id}`);
      toast.success('Change request deleted');
      if (closeDetail) setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete change request');
    }
  };

  const downloadAttachment = (att) => {
    const a = document.createElement('a');
    a.href = `data:${att.mime_type};base64,${att.data_base64}`;
    a.download = att.filename;
    a.click();
  };

  return (
    <div>
      <PageHeader
        icon={FileEdit}
        title="Change Requests"
        subtitle={isClient ? 'Submit changes to your active projects.' : 'Review and decide on client-submitted requests.'}
        actions={(
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-cr-btn" className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold"><Plus className="h-4 w-4 mr-1" /> New Request</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg">
              <DialogHeader><DialogTitle className="text-white">New change request</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Project</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="cr-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                    <SelectContent className="bg-[#0a1628] border-amber-500/20">{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.client_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="cr-title" /></div>
                <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="cr-desc" /></div>
                <div>
                  <Label>Attachments</Label>
                  <Input type="file" multiple onChange={(e) => setForm({ ...form, files: Array.from(e.target.files || []) })} className="bg-[#0f2240] border-amber-500/20" />
                  {form.files.length > 0 && <div className="text-xs text-slate-400 mt-1">{form.files.length} file(s) selected</div>}
                </div>
              </div>
              <DialogFooter><Button onClick={submit} disabled={!form.project_id || !form.title || !form.description} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="cr-submit">Submit request</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {items.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">No change requests yet.</Card>
      ) : (
        <div className="space-y-3">
          {items.map((cr) => (
            <Card key={cr.id} className="shine-card p-4 rounded-xl" data-testid={`cr-card-${cr.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-bold text-white">{cr.title}</h3>
                  <div className="text-xs text-amber-300 mt-0.5">{cr.project_name} · {cr.client_name}</div>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-2">{cr.description}</p>
                  <div className="text-xs text-slate-500 mt-2 flex flex-wrap items-center gap-2">
                    <span>By {cr.submitted_by_name}</span>
                    <span>· {new Date(cr.created_at).toLocaleDateString()}</span>
                    {(cr.attachments || []).length > 0 && <span className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" /> {cr.attachments.length}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <Badge variant="outline" className={STATUS_COLOR[cr.status]}>{cr.status}</Badge>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => openDetail(cr)} className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
                    {isOwner && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => remove(cr)}
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                        data-testid={`cr-delete-btn-${cr.id}`}
                        title="Delete change request (admin only)"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">{detail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={STATUS_COLOR[detail.status]}>{detail.status}</Badge>
                  <Badge variant="outline" className="border-amber-500/40 text-amber-200">{detail.project_name}</Badge>
                </div>
                <div className="text-sm text-slate-300 whitespace-pre-line">{detail.description}</div>
                {(detail.attachments || []).length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Attachments</Label>
                    <ul className="mt-2 space-y-1">
                      {detail.attachments.map((a, i) => (
                        <li key={i}>
                          <button onClick={() => downloadAttachment(a)} className="text-sm text-amber-300 hover:text-amber-200 inline-flex items-center gap-2">
                            <Paperclip className="h-3.5 w-3.5" /> {a.filename}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {detail.decision_note && (
                  <div className="rounded-lg bg-[#0f2240] border border-amber-500/20 p-3">
                    <div className="text-xs uppercase tracking-wider text-amber-300">Decision note</div>
                    <div className="text-sm text-slate-200 mt-1">{detail.decision_note}</div>
                    {detail.decided_by_name && <div className="text-xs text-slate-500 mt-1">By {detail.decided_by_name} · {new Date(detail.decided_at).toLocaleString()}</div>}
                  </div>
                )}
                {isManager && (
                  <div className="border-t border-amber-500/10 pt-3 space-y-2">
                    <Label className="text-white">Update status</Label>
                    <Select value={decision.status} onValueChange={(v) => setDecision({ ...decision, status: v })}>
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">{CR_STATUSES.filter((s) => s !== 'Submitted').map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Textarea placeholder="Decision note (optional)" value={decision.decision_note} onChange={(e) => setDecision({ ...decision, decision_note: e.target.value })} className="bg-[#0f2240] border-amber-500/20" />
                  </div>
                )}
              </div>
              {(isManager || isOwner) && (
                <DialogFooter className="gap-2">
                  {isOwner && (
                    <Button
                      onClick={() => remove(detail, true)}
                      variant="outline"
                      className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                      data-testid="cr-detail-delete-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  )}
                  {isManager && (
                    <Button onClick={decide} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="cr-decide-btn">Save decision</Button>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
